import { KeyValue } from '@angular/common';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { YES_NO_VALUES } from 'src/app/core/constants';
import { YesNoValue } from 'src/app/core/enums';
import { IMasterValue } from 'src/app/core/models/api/master';
import {
  IResponseMessage,
  IResponseOutputDto
} from 'src/app/core/models/api/sane-project/arpa-model';

import { from, merge, Observable, of, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, shareReplay, switchMap } from 'rxjs/operators';

const yesNoMasterValues: IMasterValue[] = YES_NO_VALUES.map(
  (kv: KeyValue<YesNoValue, string>) => ({
    id: kv.key.toString(),
    description: kv.value
  })
);

export const yesNoValues: IMasterValue[] = [
  { id: YesNoValue.Yes.toString(), description: 'Sí' },
  { id: YesNoValue.No.toString(), description: 'No' }
];

export interface IControlConfig {
  defaultValue: unknown;
  validators?: ValidatorFn[];
  disabled?: boolean;
  isSelect?: boolean;
  selectKey?: string;
}

export interface IProductModelPreset {
  name: string;
  controls: Record<string, IControlConfig>;
  masterValues?: Record<string, IMasterValue[]>;
  numericKeys?: string[];
}

export class CalculatorProductViewModel {
  public readonly form: FormGroup;
  public readonly preset: IProductModelPreset;
  public masterValues: Record<string, IMasterValue[]>;
  public readonly numericKeys: string[];

  public bonus = 0;
  public chargeable = true;
  public productName = '';
  public trace = '';
  public maximumCapital = 0;
  public hasTelesubscription?: boolean;
  public telesubscriptionDirect?: boolean;
  public telesubscriptionSingleQuestion?: boolean;
  public summaryCapitals = '';

  public messages: IResponseMessage[] = [];
  public readonly changes$: Observable<any>;

  constructor(preset: IProductModelPreset) {
    this.preset = preset;
    this.numericKeys = preset.numericKeys ?? [];
    this.masterValues = preset.masterValues ? { ...preset.masterValues } : {};

    const group: Record<string, FormControl> = {};
    for (const [key, cfg] of Object.entries(preset.controls)) {
      group[key] = new FormControl(
        { value: cfg.defaultValue, disabled: cfg.disabled ?? false },
        cfg.validators ?? []
      );
    }
    this.form = new FormGroup(group);

    this.changes$ = this.buildChangesStream().pipe(shareReplay({ bufferSize: 1, refCount: true }));
  }

  public connect(
    runCalc: () => Promise<unknown> | Observable<unknown>,
    onError?: (e: unknown) => void
  ): Subscription {
    return this.changes$
      .pipe(
        switchMap(() =>
          from(runCalc()).pipe(
            catchError(err => {
              onError?.(err);
              return of(null);
            })
          )
        )
      )
      .subscribe();
  }

  public getControl(name: string): AbstractControl {
    return this.form.get(name)!;
  }

  public getValue<T = any>(name: string): T {
    return this.getControl(name).value as T;
  }

  public setMasterValues(key: string, values: IMasterValue[]): void {
    this.masterValues[key] = values;
  }

  public getMasterValues(key: string): IMasterValue[] {
    return this.masterValues[key] || [];
  }

  public get selectKeys(): string[] {
    return Object.values(this.preset.controls)
      .filter(c => c.isSelect && !!c.selectKey)
      .map(c => c.selectKey!) as string[];
  }

  public joinHtml(msgs: IResponseMessage[]): string {
    return (msgs || []).map(m => m.html || '').join('');
  }

  public fromResponse(res: IResponseOutputDto): void {
    this.bonus = res.bonus ?? 0;
    this.chargeable = !!res.chargeable;
    this.productName = res.productName || '';
    this.trace = res.trace || '';
    this.maximumCapital = Number(res.maximumCapital ?? 0);
    this.hasTelesubscription = !!res.hasTelesubscription;
    this.telesubscriptionDirect = !!res.telesubscriptionDirect;
    this.telesubscriptionSingleQuestion = !!res.telesubscriptionSingleQuestion;
    this.summaryCapitals = res.summaryCapitals || '';
    this.messages = Array.isArray(res.messages) ? [...res.messages] : [];
  }

  private getNumericControlNames(): string[] {
    const allNames = Object.keys(this.preset.controls);

    const start = this.numericKeys?.length ? this.numericKeys : allNames;

    const isNumberLike = (name: string): boolean => {
      const cfg = this.preset.controls[name];
      if (!cfg || cfg.isSelect) { return false; }

      if (typeof cfg.defaultValue === 'number') { return true; }

      const ctrl = this.form.get(name);
      if (!ctrl) { return false; }
      const raw = ctrl.value;
      if (typeof raw === 'number') { return true; }
      if (typeof raw === 'string') {
        const n = Number(raw.replace(',', '.'));
        return Number.isFinite(n);
      }

      const v = cfg.validators || [];
      const fnNames = v.map(fn => (fn as any)?.name ?? '').join('|').toLowerCase();
      if (fnNames.includes('min') || fnNames.includes('max') || fnNames.includes('pattern')) {
        return true;
      }

      return false;
    };

    return start
      .filter(name => allNames.includes(name))
      .filter(name => !this.preset.controls[name]?.isSelect)
      .filter(isNumberLike);
  }

  public hasAnyPositiveNumeric(): boolean {
    const numericNames = this.getNumericControlNames();
    if (!numericNames.length) { return false; }

    for (const name of numericNames) {
      const ctrl = this.form.get(name);
      if (!ctrl || ctrl.disabled) { continue; }
      const raw = ctrl.value;
      const num = typeof raw === 'number' ? raw : Number(String(raw ?? '').replace(',', '.'));
      if (Number.isFinite(num) && num > 0) {
        return true;
      }
    }
    return false;
  }


  private deepEqual(a: unknown, b: unknown): boolean {
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      return a === b;
    }
  }

  private buildChangesStream(): Observable<any> {
    const entries = Object.entries(this.form.controls);
    const numericNames = new Set(this.getNumericControlNames());

    const streams = entries.map(([name, ctrl]) => {
      const isNumeric = numericNames.has(name);
      return ctrl.valueChanges.pipe(
        isNumeric ? debounceTime(400) : debounceTime(0),
        map(() => this.form.getRawValue())
      );
    });

    return merge(...streams).pipe(
      filter(() => this.form.valid),
      distinctUntilChanged((a, b) => this.deepEqual(a, b))
    );
  }
}

export const accidentPresets: Record<string, IProductModelPreset> = {
  axa: {
    name: 'axa',
    controls: {
      aAxaCapitalDeath: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      aAxaCapitalDeathTrafficAccident: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      aAxaCapitalHeartAttack: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      aAxaCapitalDisabilityType: { defaultValue: '015', isSelect: true, selectKey: 'S_AXA_CAPITALDISABILITYTYPE' },
      aAxaCapitalDisability: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      aAxaCapitalPermanentDisabilityTrafficAccident: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      aAxaCapitalMedicalExpensesForAccidents: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      aAxaCapitalHospitalizationIllnessAccident: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      aAxaCapitalFamilyAccidentAssistance: { defaultValue: '0', isSelect: true, selectKey: 'S_AXA_CAPITALFAMILYACCIDENTASSISTANCE' },
      aAxaCapitalHIVHepatitisInfection: { defaultValue: 0, disabled: true, validators: [Validators.min(0), Validators.max(9_999_999)] }
    },
    numericKeys: [
      'aAxaCapitalDeath',
      'aAxaCapitalDeathTrafficAccident',
      'aAxaCapitalHeartAttack',
      'aAxaCapitalDisability',
      'aAxaCapitalPermanentDisabilityTrafficAccident',
      'aAxaCapitalMedicalExpensesForAccidents',
      'aAxaCapitalHospitalizationIllnessAccident',
      'aAxaCapitalHIVHepatitisInfection'
    ]
  },
  surne: {
    name: 'surne',
    controls: {
      aSurneCapitalDeath: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      aSurneCapitalHeartAttack: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      aSurneCapitalAbsoluteDisabilityType: { defaultValue: '0', isSelect: true, selectKey: 'S_SURNE_CAPITALABSOLUTEDISABILITYTYPE' },
      aSurneCapitalAbsoluteDisability: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      aSurneCapitalTotalPermanentDisability: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      aSurneCapitalHealthDisabilityType: { defaultValue: '0', isSelect: true, selectKey: 'S_SURNE_CAPITALHEALTHDISABILITYTYPE' },
      aSurneRateIdHIVHepatitisInfection: { defaultValue: '0', isSelect: true, selectKey: 'S_SURNE_CAPITALHIVHEPATITISINFECTION' }
    },
    numericKeys: [
      'aSurneCapitalDeath',
      'aSurneCapitalHeartAttack',
      'aSurneCapitalAbsoluteDisability',
      'aSurneCapitalTotalPermanentDisability'
    ]
  },
  prebal: {
    name: 'prebal',
    controls: {
      aPrebalCapitalDeath: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      aPrebalCapitalDeathTrafficAccident: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      aPrebalCapitalDisabilityType: { defaultValue: '0', isSelect: true, selectKey: 'S_PREBAL_CAPITALDISABILITYTYPE' },
      aPrebalCapitalDisability: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      aPrebalHasSelectedCapitalDisability: { defaultValue: '0', isSelect: true, selectKey: 'S_PREBAL_HASSELECTEDCAPITALDISABILITY' },
      aPrebalRateIdHIVHepatitisInfection: {
        defaultValue: '0',
        isSelect: true,
        selectKey: 'S_PREBAL_CAPITALHIVHEPATITISINFECTION',
        validators: [Validators.min(0), Validators.max(9_999_999)]
      },
      capitalHealthCareAccidents: { defaultValue: YesNoValue.No.toString(), isSelect: true, selectKey: 'yesNoValues' }
    },
    masterValues: {
      yesNoValues: yesNoMasterValues
    },
    numericKeys: [
      'aPrebalCapitalDeath',
      'aPrebalCapitalDeathTrafficAccident',
      'aPrebalCapitalDisability'
    ]
  }
};

export const lifePresets: Record<string, IProductModelPreset> = {
  allianz: {
    name: 'lifeAllianz',
    controls: {
      vAllianzCapitalDeath: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      vAllianzCapitalIptp: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      vAllianzCapitalIap: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] }
    },
    numericKeys: ['vAllianzCapitalDeath', 'vAllianzCapitalIptp', 'vAllianzCapitalIap']
  },
  axa: {
    name: 'lifeAxa',
    controls: {
      vAxaCapitalDeath: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      vAxaCapitalIptp: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      vAxaCapitalIap: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      vAxaCapitalSevereIllness: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] }
    },
    numericKeys: ['vAxaCapitalDeath', 'vAxaCapitalIptp', 'vAxaCapitalIap', 'vAxaCapitalSevereIllness']
  },
  staLucia: {
    name: 'lifeStaLucia',
    controls: {
      vStaLuciaCapitalDeath: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      vStaLuciaCapitalIptp: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      vStaLuciaCapitalIap: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] }
    },
    numericKeys: ['vStaLuciaCapitalDeath', 'vStaLuciaCapitalIptp', 'vStaLuciaCapitalIap']
  },
  surne: {
    name: 'lifeSurne',
    controls: {
      vSurneCapitalDeath: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      vSurneCapitalIptp: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      vSurneCapitalIap: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      vSurneMonthlyInvalidity: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] }
    },
    numericKeys: ['vSurneCapitalDeath', 'vSurneCapitalIptp', 'vSurneCapitalIap', 'vSurneMonthlyInvalidity']
  },
  surneLifePro: {
    name: 'lifeSurneLifePro',
    controls: {
      vSurneLifeProCapitalDeath: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      vSurneLifeProCapitalIptp: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      vSurneLifeProCapitalIap: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] }
    },
    numericKeys: ['vSurneLifeProCapitalDeath', 'vSurneLifeProCapitalIptp', 'vSurneLifeProCapitalIap']
  },
  helvetia: {
    name: 'lifeHelvetia',
    controls: {
      vHelvetiaCapitalDeath: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      vHelvetiaCapitalIptp: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      vHelvetiaCapitalIap: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] }
    },
    numericKeys: ['vHelvetiaCapitalDeath', 'vHelvetiaCapitalIptp', 'vHelvetiaCapitalIap']
  },
  prebal: {
    name: 'lifePrebal',
    controls: {
      vPrebalCapitalDeath: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      vPrebalCapitalIptp: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      vPrebalCapitalIap: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] }
    },
    numericKeys: ['vPrebalCapitalDeath', 'vPrebalCapitalIptp', 'vPrebalCapitalIap']
  },
  prebalInvPro: {
    name: 'lifePrebalInvPro',
    controls: {
      vPrebalInvProCapitalIptp: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      vPrebalInvProCapitalIap: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] }
    },
    numericKeys: ['vPrebalInvProCapitalIptp', 'vPrebalInvProCapitalIap']
  },
  zurich: {
    name: 'lifeZurich',
    controls: {
      vZurichCapitalDeath: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      vZurichCapitalIptp: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      vZurichCapitalIap: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] }
    },
    numericKeys: ['vZurichCapitalDeath', 'vZurichCapitalIptp', 'vZurichCapitalIap']
  }
};

export const itPresets: Record<string, IProductModelPreset> = {
  pmElite: {
    name: 'itPmElite',
    controls: {
      itPmEliteSubsidy: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      itPmEliteFranchise: { defaultValue: '0', isSelect: true, selectKey: 'itPmEliteFranchise' },
      itPmEliteDuration: { defaultValue: '365', isSelect: true, selectKey: 'itPmEliteDuration' },
      itPmEliteHospitalization: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] }
    },
    numericKeys: ['itPmEliteSubsidy', 'itPmEliteHospitalization']
  },
  pmCoeg: {
    name: 'itPmCoeg',
    controls: {
      itPmCoegSubsidy: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      itPmCoegFranchise: { defaultValue: '0', isSelect: true, selectKey: 'itPmCoegFranchise' },
      itPmCoegDuration: { defaultValue: '365', isSelect: true, selectKey: 'itPmCoegDuration' }
    },
    numericKeys: ['itPmCoegSubsidy']
  },
  surnePlus: {
    name: 'itSurnePlus',
    controls: {
      itSurnePlusSubsidy: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      itSurnePlusFranchise: { defaultValue: '0', isSelect: true, selectKey: 'itSurnePlusFranchise' },
      itSurnePlusDuration: { defaultValue: '365', isSelect: true, selectKey: 'itSurnePlusDuration' },
      itSurnePlusHospitalization: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] }
    },
    numericKeys: ['itSurnePlusSubsidy', 'itSurnePlusHospitalization']
  },
  surneVip: {
    name: 'itSurneVip',
    controls: {
      itSurneVipSubsidy: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      itSurneVipFranchise: { defaultValue: '0', isSelect: true, selectKey: 'itSurneVipFranchise' },
      itSurneVipDuration: { defaultValue: '365', isSelect: true, selectKey: 'itSurneVipDuration' }
    },
    numericKeys: ['itSurneVipSubsidy']
  },
  prebal: {
    name: 'itPrebal',
    controls: {
      itPrebalSubsidy: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      itPrebalFranchise: { defaultValue: '0', isSelect: true, selectKey: 'itPrebalFranchise' },
      itPrebalDuration: { defaultValue: '365', isSelect: true, selectKey: 'itPrebalDuration' },
      itPrebalHospitalization: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      itPrebalHasLife: { defaultValue: '0', isSelect: true, selectKey: 'itPrebalHasLife' }
    },
    numericKeys: ['itPrebalSubsidy', 'itPrebalHospitalization']
  }
};

export const wholeLifePresets: Record<string, IProductModelPreset> = {
  staLucia: {
    name: 'lifeWholeStaLucia',
    controls: {
      veStaLuciaCapitalDeath: { defaultValue: 0, validators: [Validators.min(0), Validators.max(9_999_999)] },
      veStaLuciaCapitalIap: { defaultValue: null, isSelect: true, selectKey: 'selectCapitalIapList' },
      veStaLuciaCapitalPay: { defaultValue: null, isSelect: true, selectKey: 'selectCapitalPayList' },
      veStaLuciaCapitalSmoker: { defaultValue: null, isSelect: true, selectKey: 'selectCapitalSmokerList' }
    },
    masterValues: {
      selectCapitalIapList: [],
      selectCapitalPayList: [],
      selectCapitalSmokerList: []
    },
    numericKeys: ['veStaLuciaCapitalDeath']
  }
};

export const dependencePresets: Record<string, IProductModelPreset> = {
  axa: {
    name: 'axa',
    controls: {
      depAxaSevereDependency: { defaultValue: '0', isSelect: true, selectKey: 'S_DEP_AXA_SEVEREDEPENDENCY' },
      depAxaGreatDependency: { defaultValue: '', disabled: true }
    },
    numericKeys: []
  },
  surne: {
    name: 'surne',
    controls: {
      depSurneCapitalIncome: { defaultValue: '', isSelect: true, selectKey: 'S_DEP_SURNE_CAPITALINCOME' },
      depSurneSevereDependency: { defaultValue: '', isSelect: true, selectKey: 'S_DEP_SURNE_SEVEREDEPENDENCY' },
      depSurneGreatDependency: { defaultValue: 0, disabled: true }
    },
    numericKeys: []
  }
};

export const deathsPresets: Record<string, IProductModelPreset> = {
  aura: {
    name: 'aura',
    controls: {
      decAuraSelectChargeable: { defaultValue: '0', isSelect: true, selectKey: 'S_DEC_AURA_SELECT_CHARGEABLE' },
      decAuraTaxAdvice: { defaultValue: 1000, disabled: true },
      decAuraBurial: { defaultValue: 4500, disabled: true }
    },
    numericKeys: []
  }
};

export const allPresets = {
  accidents: accidentPresets,
  life: lifePresets,
  it: itPresets,
  wholeLife: wholeLifePresets,
  dependence: dependencePresets,
  deaths: deathsPresets
};
